import { Injectable } from '@angular/core';
import {
  DocumentExtractConfig,
  DocumentExtractSchema
} from '../models/document-extract/document-extract.model';
import { DOCUMENT_EXTRACT_SCHEMAS } from '../models/document-extract/document-extract-schemas';
import { DOCUMENT_EXTRACT_ERRORS } from '../constants/document-extract.constants';

/**
 * DocumentExtractSchemaService
 * 
 * Service for managing and retrieving document extraction schemas.
 * Provides a centralized registry of all supported document types and their extraction configurations.
 * 
 * This service acts as a schema repository, making it easy to:
 * - Get extraction schemas for specific document types
 * - Retrieve dropdown options for semantic matching
 * - Check if a document type is supported
 * - Get metadata about document types
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentExtractSchemaService {
  private readonly schemaRegistry: Record<string, DocumentExtractConfig> = DOCUMENT_EXTRACT_SCHEMAS;

  constructor() {}

  /**
   * Gets the complete extraction configuration for a document type
   * 
   * @param documentType The document type identifier
   * @returns DocumentExtractConfig The complete configuration including schema and dropdown options
   * @throws Error if document type is not found
   */
  getConfigForDocumentType(documentType: string): DocumentExtractConfig {
    const config = this.schemaRegistry[documentType];
    
    if (!config) {
      throw new Error(`${DOCUMENT_EXTRACT_ERRORS.SCHEMA_NOT_FOUND}: ${documentType}`);
    }
    
    return config;
  }

  /**
   * Gets the extraction schema for a document type
   * 
   * @param documentType The document type identifier
   * @returns DocumentExtractSchema The extraction schema mapping field keys to types
   * @throws Error if document type is not found
   */
  getSchemaForDocumentType(documentType: string): DocumentExtractSchema {
    const config = this.getConfigForDocumentType(documentType);
    return config.schema;
  }

  /**
   * Gets the dropdown options for a document type (if any)
   * 
   * @param documentType The document type identifier
   * @returns Record<string, any[]> | undefined The dropdown options or undefined if none exist
   */
  getDropdownOptionsForDocumentType(documentType: string): Record<string, any[]> | undefined {
    try {
      const config = this.getConfigForDocumentType(documentType);
      return config.dropdownOptions;
    } catch (error) {
      return undefined;
    }
  }

  /**
   * Gets the source authority for a document type
   * 
   * @param documentType The document type identifier
   * @returns string | undefined The source authority or undefined
   */
  getSourceAuthorityForDocumentType(documentType: string): string | undefined {
    try {
      const config = this.getConfigForDocumentType(documentType);
      return config.sourceAuthority;
    } catch (error) {
      return undefined;
    }
  }

  /**
   * Gets the display name for a document type
   * 
   * @param documentType The document type identifier
   * @returns string | undefined The display name or undefined
   */
  getDisplayNameForDocumentType(documentType: string): string | undefined {
    try {
      const config = this.getConfigForDocumentType(documentType);
      return config.displayName;
    } catch (error) {
      return undefined;
    }
  }

  /**
   * Checks if a document type is supported for extraction
   * 
   * @param documentType The document type identifier
   * @returns boolean True if the document type is supported, false otherwise
   */
  isDocumentTypeSupported(documentType: string): boolean {
    return !!this.schemaRegistry[documentType];
  }

  /**
   * Gets a list of all supported document types
   * 
   * @returns string[] Array of all supported document type identifiers
   */
  getAllSupportedDocumentTypes(): string[] {
    return Object.keys(this.schemaRegistry);
  }

  /**
   * Gets a list of all document configurations
   * 
   * @returns DocumentExtractConfig[] Array of all document configurations
   */
  getAllDocumentConfigs(): DocumentExtractConfig[] {
    return Object.values(this.schemaRegistry);
  }

  /**
   * Gets document types filtered by category
   * 
   * @param category The category to filter by (e.g., 'Training', 'DQF', 'Driver')
   * @returns DocumentExtractConfig[] Array of matching document configurations
   */
  getDocumentTypesByCategory(category: string): DocumentExtractConfig[] {
    return this.getAllDocumentConfigs().filter(config => 
      config.displayName?.toLowerCase().includes(category.toLowerCase()) ||
      config.documentType?.toLowerCase().includes(category.toLowerCase())
    );
  }

  /**
   * Validates if a schema object is valid
   * 
   * @param schema The schema to validate
   * @returns boolean True if valid, false otherwise
   */
  isValidSchema(schema: DocumentExtractSchema): boolean {
    if (!schema || typeof schema !== 'object') {
      return false;
    }

    const validTypes = ['string', 'date', 'number', 'boolean', 'select'];
    
    return Object.values(schema).every(type => 
      validTypes.includes(type as string)
    );
  }

  /**
   * Gets field names from a schema
   * 
   * @param documentType The document type identifier
   * @returns string[] Array of field names in the schema
   */
  getSchemaFieldNames(documentType: string): string[] {
    try {
      const schema = this.getSchemaForDocumentType(documentType);
      return Object.keys(schema);
    } catch (error) {
      return [];
    }
  }

  /**
   * Checks if a specific field exists in a document type's schema
   * 
   * @param documentType The document type identifier
   * @param fieldName The field name to check
   * @returns boolean True if the field exists, false otherwise
   */
  hasField(documentType: string, fieldName: string): boolean {
    const fieldNames = this.getSchemaFieldNames(documentType);
    return fieldNames.includes(fieldName);
  }
}

